// #22 LevelCard and LevelGroup: the framework's level cards as one radiogroup (alt drew loose role=radio tiles with no
// group; this fixes that gap). Each card carries his display name (Manager & Below · Director · VP+) over the decision
// band of the logic copy (Sr Director and below · VP+) and a bar that takes the blue-to-purple fill when chosen. One
// tab stop, arrows move and select. The locked form (I-2's framework banner) is a KeyValue whose value is carried,
// with the lock, from the approved request; `mode="view"` is the same values-only form without the lock.

import type { ReactNode } from "react";
import { Tooltip } from "../lib/Tooltip";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useSvgId } from "../lib/useSvgId";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { LEVEL_BAND, LEVEL_DISPLAY, type Level } from "../labels";
import type { Blocked } from "../core/Button";
import { KeyValue } from "../core/KeyValue";
import { useChoice, type ChoiceItem } from "./choice";
import { SCORECARD_LABELS } from "./labels";

export const LEVEL_ORDER: readonly Level[] = ["manager_and_below", "director_srdirector", "vp_and_above"];

export interface LevelCardProps {
  level: Level;
  /** The line under the name; default the level's decision band (Sr Director and below), unless it repeats the name. */
  sub?: ReactNode | undefined;
  checked?: boolean | undefined;
  blocked?: boolean | undefined;
  /** The radio wiring a LevelGroup hands its cards (role, aria-checked, roving tab stop and handlers). */
  radio?: ChoiceItem | undefined;
  describedBy?: string | undefined;
  hs?: Hs | undefined;
}

/** One level card. Inside a LevelGroup it is a radio; alone it is a static card (the chosen level, shown). */
export function LevelCard({ level, sub, checked, blocked, radio, describedBy, hs }: LevelCardProps) {
  const hot = useHs(hs);
  // The band line, unless it only repeats the name (VP+ is both the display name and the band).
  const line = sub ?? (LEVEL_BAND[level] === LEVEL_DISPLAY[level] ? null : LEVEL_BAND[level]);
  return (
    <div
      {...radio}
      className={cx("mc-lvl", checked && "mc-on", blocked && "mc-dis")}
      aria-disabled={radio && blocked ? true : undefined}
      aria-describedby={radio ? describedBy : undefined}
      {...hot}
    >
      <b className="mc-lvl-n">{LEVEL_DISPLAY[level]}</b>
      {line !== null ? <span className="mc-lvl-s">{line}</span> : null}
      <span className="mc-lvl-bar" aria-hidden="true" />
    </div>
  );
}

export interface LevelGroupProps {
  /** The group's name (a label: the field it sets). Default "Level". */
  label?: string | undefined;
  value?: Level | null | undefined;
  defaultValue?: Level | null | undefined;
  onChange?: ((level: Level) => void) | undefined;
  levels?: readonly Level[] | undefined;
  subs?: Partial<Record<Level, ReactNode>> | undefined;
  /** The locked form: the level is carried from the approved request (the node names it), shown as a KeyValue. */
  locked?: { from: ReactNode } | undefined;
  can?: boolean | undefined;
  blocked?: Blocked | undefined;
  mode?: "edit" | "view" | undefined;
  hs?: Partial<Record<Level, Hs>> | undefined;
  className?: string | undefined;
}

export function LevelGroup({
  label = SCORECARD_LABELS.level, value, defaultValue, onChange, levels = LEVEL_ORDER, subs, locked, can, blocked, mode, hs, className,
}: LevelGroupProps) {
  const id = useSvgId();
  const choice = useChoice({ values: levels, value, defaultValue, onChange, isBlocked: () => blocked !== undefined });
  if (can === false) return null;
  if (locked || mode === "view") {
    const shown = choice.chosen ? LEVEL_DISPLAY[choice.chosen] : "—";
    return (
      <KeyValue
        className={cx("mc-lvl-lock", className)}
        cols={1}
        items={[locked ? [label, shown, locked.from] : [label, shown]]}
      />
    );
  }
  const why = `${id}-why`;
  const group = (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx("mc-lvls", className)}
      {...choice.container}
    >
      {levels.map((level, at) => (
        <LevelCard
          key={level}
          level={level}
          sub={subs?.[level]}
          checked={at === choice.index}
          blocked={blocked !== undefined}
          radio={choice.item(at)}
          describedBy={blocked ? why : undefined}
          hs={hs?.[level]}
        />
      ))}
    </div>
  );
  if (!blocked) return <div className="mc-lvls-w">{group}</div>;
  return (
    <div className="mc-lvls-w">
      <Tooltip label={blocked.reason} mode="mirror">{group}</Tooltip>
      <VisuallyHidden id={why}>{blocked.reason}</VisuallyHidden>
    </div>
  );
}
